import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Button, Typography, Grid, TextField, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Paper, IconButton, MenuItem as MuiMenuItem, Menu } from '@material-ui/core';
import { ArrowBack, ArrowForward, MoreVert } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import AddCohort from "./AddCohort";
import CohortDetails from "./CohortDetails";
import './adminDashboard.css';
import ThemeToggle from './ThemeToggle'; // adjust path if needed
import Reports from "./Reports";
import CohortManagement from "./CohortManagement";
import InviteStudents from "./InviteStudents";

export default function AdminDashboard(props) {
  // Using CSS classes instead of makeStyles
  const classes = {
    root: 'ad-root',
    sidebar: 'ad-sidebar',
    menuItem: 'ad-menuItem',
    mainContent: 'ad-mainContent',
    formControl: 'ad-formControl',
    button: 'ad-button',
    table: 'ad-table',
    tableContainer: 'ad-tableContainer',
    tableHead: 'ad-tableHead',
    tableCell: 'ad-tableCell',
    error: 'ad-error',
    pagination: 'ad-pagination',
    tableRow: 'ad-tableRow',
  };

  const [currentSection, setCurrentSection] = useState("users");
  const [user, setUser] = useState([]);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [cohortDetailsOpen, setCohortDetailsOpen] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [userStats, setUserStats] = useState({
    totalAccounts: '',
    recentAccounts: '',
    totalSurveyC: '',
    recentSurveyC: '',
  });
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const history = useHistory();

  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedCohortForEmail, setSelectedCohortForEmail] = useState(null);

  // Menu state for actions dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCohort, setMenuCohort] = useState(null);

  const allowedEmails = [
    'grahamfellows@gmail.com',
    'ash.knight151203@gmail.com',
    'janmendoza85@gmail.com',
    'elizaaajane03@gmail.com',
    'sarsh1968@gmail.com',
    'sasirekhavaikuntam@gmail.com'
  ];

  useEffect(() => {
    getUserStats();
    getUserQuery();
    checkAuthorization();
  }, []);

  useEffect(() => {
    getCohorts();
  }, []);

  const checkAuthorization = async () => {
    try {
      const response = await fetch('/api/user/getCurrentUserEmail', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('User not authenticated');
      const userData = await response.json();
      setIsAuthorized(allowedEmails.includes(userData.email));
    } catch (error) {
      console.error('Error checking authorization:', error);
      setIsAuthorized(false);
    } finally {
      setAuthChecked(true);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    let query = '';
    if (selectedOption === 'Email') {
      query = `email=${email.slice(0, 100).trim()}`;
    } else if (selectedOption === 'Name') {
      query = `name=${name.slice(0, 100).trim()}`;
    }
    await getUserQuery(query);
  };

  const getUserQuery = async (query = '') => {
    try {
      const response = await fetch(`/api/user/getUsers?${query}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('User not found');
      const userData = await response.json();
      setUser(userData.users);
      setError(null);
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser([]);
      setError('User not found');
    }
  };

  const getUserStats = async () => {
    try {
      const response = await fetch(`/api/user/getUserStats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('User stats not found');
      const userData = await response.json();
      setUserStats(userData.users);
      setError(null);
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats(null);
      setError('User stats not found');
    }
  };

  const handleInputChange = (setter) => (event) => setter(event.target.value);

  const handlePageChange = (newPage) => setPage(newPage);

  const totalPages = Math.ceil(user.length / rowsPerPage);
  const usersToShow = user.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Cohorts
  const [cohortPage, setCohortPage] = useState(1);
  const cohortPageSize = 10;
  const handleCohortPageChange = (newPage) => setCohortPage(newPage);

  const filteredCohorts = useMemo(() => {
    if (!searchTerm.trim()) return cohorts;
    const term = searchTerm.toLowerCase().trim();
    return cohorts.filter(cohort => cohort.name.toLowerCase().includes(term));
  }, [cohorts, searchTerm]);

  const totalCohortPages = Math.ceil(filteredCohorts.length / cohortPageSize);
  const cohortsToShow = filteredCohorts.slice(
    (cohortPage - 1) * cohortPageSize,
    cohortPage * cohortPageSize
  );

  const handleCohortSearch = (event) => {
    setSearchTerm(event.target.value);
    setCohortPage(1);
  };

  const [openAddCohort, setOpenAddCohort] = useState(false);
  const handleOpenAddCohort = () => setOpenAddCohort(true);
  const handleCloseAddCohort = () => setOpenAddCohort(false);

  const handleSaveCohort = async (newCohort) => {
    try {
      console.log("Saving cohort with data:", newCohort);
      const response = await fetch(`/api/cohort`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newCohort),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const savedCohort = await response.json();
      setCohorts((prev) => [...prev, savedCohort]);
      setOpenAddCohort(false);

      // Refresh the cohorts list
      getCohorts();
    } catch (error) {
      console.error("Error adding cohort:", error);
    }
  };

  // Cohort details (modal)
  const handleCohortViewDetails = (cohort) => {
    setSelectedCohort(cohort);
    setCohortDetailsOpen(true);
  };

  const handleCloseCohortDetails = () => {
    setCohortDetailsOpen(false);
    setSelectedCohort(null);
  };

  const getCohorts = async () => {
    try {
      const response = await fetch(`/api/cohort`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Cohort stats not found');
      const cohortData = await response.json();
      setCohorts(cohortData);
      setError(null);
    } catch (error) {
      console.error('Error fetching cohorts: ', error);
      setCohorts([]);
      setError('No cohorts found.');
    }
  };

  // Fetch invite code for a cohort if not present
  const ensureInviteCode = async (cohort) => {
    if (cohort.invite_code) return cohort.invite_code;
    try {
      const res = await fetch(`/api/cohort/${cohort.id}/invite`, { method: 'GET' });
      if (!res.ok) throw new Error('Failed to fetch invite code');
      const data = await res.json();
      const code = data.invite_code;
      // Update local state with code so table reflects it next render
      setCohorts(prev => prev.map(c => c.id === cohort.id ? { ...c, invite_code: code } : c));
      return code;
    } catch (e) {
      console.error('Error getting invite code', e);
      return '';
    }
  };

  // cohort actions
  const handleMenuOpen = (event, cohort) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuCohort(cohort);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCohort(null);
  };

  const handleCopySurveyLink = async (cohort) => {
    const surveyLink = `${window.location.origin}/questions?cohortId=${cohort.id}`;
    try {
      await navigator.clipboard.writeText(surveyLink);
      alert(`Copied survey link for ${cohort.name}`);
    } catch (e) {
      alert('Failed to copy link');
    }
    handleMenuClose();
  };

  const handleEmailMembers = (cohort) => {
    setSelectedCohortForEmail(cohort);
    setEmailModalOpen(true);
    handleMenuClose();
  };

  const handleCopyInviteCode = async (cohort) => {
    const code = await ensureInviteCode(cohort);
    if (code) {
      navigator.clipboard.writeText(code);
      alert(`Copied invite code: ${code}`);
    } else {
      alert('No invite code available.');
    }
    handleMenuClose();
  };

  const renderSection = () => {
    switch (currentSection) {
      case "users":
        return (
          <div className={classes.mainContent}>
            <Typography variant="h4" align="center">Admin Dashboard</Typography>

            <Grid container spacing={2} className="ad-metrics">
              {[
                ['Total accounts', userStats?.totalAccounts],
                ['New accounts in 30 days', userStats?.recentAccounts],
                ['Participants with completions', userStats?.totalSurveyC],
                ['Completed in the last 30 days', userStats?.recentSurveyC],
              ].map(([label, value]) => <Grid item xs={12} sm={6} lg={3} key={label}>
                <Card className="emap-admin-metric" variant="outlined"><Box p={3}>
                  <Typography variant="body2" color="textSecondary">{label}</Typography>
                  <Typography variant="h3">{value === '' ? '...' : value}</Typography>
                </Box></Card>
              </Grid>)}
            </Grid>

            <Card className={classes.tableContainer} variant="outlined">
              <Box p={2}>
                <form onSubmit={handleSearch}>
                  <Typography variant="h6">Search Users</Typography>
                  <div className="ad-search-row">
                    <TextField
                      select
                      label="Select a search type"
                      value={selectedOption}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className={classes.formControl}
                      variant="filled"
                      fullWidth
                    >
                      <MenuItem value="LoginTable">Recent Users</MenuItem>
                      <MenuItem value="Email">Email</MenuItem>
                      <MenuItem value="Name">Name</MenuItem>
                    </TextField>

                    {selectedOption === 'Email' && (
                      <TextField
                        label="Email"
                        type="text"
                        placeholder="Enter email"
                        value={email}
                        onChange={handleInputChange(setEmail)}
                        fullWidth
                        className={classes.formControl}
                        variant="filled"
                      />
                    )}

                    {selectedOption === 'Name' && (
                      <TextField
                        label="Name"
                        type="text"
                        placeholder="Enter name"
                        value={name}
                        onChange={handleInputChange(setName)}
                        fullWidth
                        className={classes.formControl}
                        variant="filled"
                      />
                    )}

                    <Button type="submit" variant="contained" className={classes.button}>
                      Search
                    </Button>
                  </div>
                </form>
              </Box>
            </Card>

            {error && <Typography className={classes.error}>{error}</Typography>}

            {user.length > 0 && (
              <Paper className={classes.tableContainer} elevation={2}>
                <Table className={classes.table} aria-label="simple table">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell}>Full Name</TableCell>
                      <TableCell className={classes.tableCell}>Email</TableCell>
                      <TableCell className={classes.tableCell}>Last Sign In</TableCell>
                      <TableCell className={classes.tableCell}>Last Survey Completed</TableCell>
                      <TableCell className={classes.tableCell}>Pro User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersToShow.map((u) => (
                      <TableRow key={u.userID}>
                        <TableCell className={classes.tableCell}>{u.fullName}</TableCell>
                        <TableCell className={classes.tableCell}>{u.email}</TableCell>
                        <TableCell className={classes.tableCell}>{u.lastSignIn}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {u.lastSurveyCompleted ? u.lastSurveyCompleted : 'Survey Not Complete'}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {u.isProUser ? 'Yes' : 'No'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className={classes.pagination}>
                  <IconButton
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    <ArrowBack />
                  </IconButton>
                  <Typography variant="body1">
                    Page {page} of {totalPages}
                  </Typography>
                  <IconButton
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    <ArrowForward />
                  </IconButton>
                </div>
              </Paper>
            )}
          </div>
        );

      case "cohorts":
        return <CohortManagement classes={classes} />;

      case "reports":
        return <Reports classes={classes} />;

      default:
        return null;
    }
  };

  if (!authChecked) {
    return <Typography variant="h6" align="center">Checking authorization...</Typography>;
  }

  if (!isAuthorized) {
    return <Typography variant="h6" align="center">You are not authorized to view this page.</Typography>;
  }

  return (
    <div className={classes.root}>
      {/* Sidebar */}
      <div className={classes.sidebar}>
        <MenuItem selected={currentSection === "users"} onClick={() => setCurrentSection("users")} className={classes.menuItem}>USERS</MenuItem>
        <MenuItem selected={currentSection === "cohorts"} onClick={() => setCurrentSection("cohorts")} className={classes.menuItem}>COHORTS</MenuItem>
        <MenuItem selected={currentSection === "reports"} onClick={() => setCurrentSection("reports")} className={classes.menuItem}>REPORTS</MenuItem>

        <div style={{ marginTop: 'auto', padding: 12 }}>
          <ThemeToggle />
        </div>
      </div>

      {/* Main content */}
      <div className={classes.mainContent}>
        {renderSection()}
      </div>
    </div>
  );
}

